import React from 'react';
import { formatISO, isAfter, isBefore, isSameDay } from 'date-fns';

import { PopoverDialog, usePopover } from '@reckon-web/popover';
import { Calendar } from '@reckon-web/date-picker';
import { Text } from '@reckon-web/text';
import { Flex } from '@reckon-web/flex';
import { SelectMenuButton } from '@reckon-web/select-menu';
import { useTheme } from '@reckon-web/theme';
import { makeId } from '@reckon-web/utils';

import { DateUtils } from '../../utils/date-utils';

type ISODate = string;
type ISODateRange = {
  start: ISODate;
  end: ISODate;
};

export const DateRangePicker: React.FC<{
  label: string;
  value: ISODateRange;
  onChange: (value: ISODateRange) => void;
  disabled?: boolean;
  testId?: string;
  isClearable?: boolean;
}> = ({
  label,
  value,
  onChange,
  disabled,
  isClearable = true,
  testId = `date-range-picker`,
}) => {
  const dates = [
    value.start && new Date(value.start),
    value.end && new Date(value.end),
  ].filter<Date>(Boolean as any);
  const setDates = (dates: Date[]) =>
    onChange({
      start:
        dates[0] &&
        (formatISO(dates[0], { representation: 'date' }) as ISODate),
      end:
        dates[1] &&
        (formatISO(dates[1], { representation: 'date' }) as ISODate),
    });

  return (
    <Popover
      label={
        <Flex alignItems="center" gap="xsmall" data={{ testid: testId }}>
          <Text
            size="small"
            weight="medium"
            color="muted"
            leading="tighter"
            data={{ testid: makeId(testId, 'dates') }}
          >
            {label}
            {dates.length > 0 && ':'}
          </Text>
          <DateRangeLabel dates={dates} />
        </Flex>
      }
      hasValue={dates.length > 0}
      onClear={isClearable ? () => setDates([]) : undefined}
      disabled={disabled}
    >
      <DateRangePickerCalendar dates={dates} setDates={setDates} />
    </Popover>
  );
};

const DateRangeLabel: React.FC<{ dates: Date[]; testId?: string }> = ({
  dates,
  testId = `date-range-label`,
}) => {
  if (dates.length === 1) {
    const [fromDate] = dates;
    return (
      <Text
        size="small"
        weight="medium"
        color="active"
        leading="tighter"
        data={{ testid: makeId(testId, 'from-date') }}
      >
        {DateUtils.formatDateShort(fromDate)}
      </Text>
    );
  }
  if (dates.length === 2) {
    const [fromDate, toDate] = dates;
    return (
      <Text
        size="small"
        weight="medium"
        color="active"
        leading="tighter"
        data={{ testid: makeId(testId, 'to-date') }}
      >
        {DateUtils.formatDatePair(fromDate, toDate)}
      </Text>
    );
  }
  return null;
};

const DateRangePickerCalendar: React.FC<{
  dates: Date[];
  setDates: (dates: Date[]) => void;
  testId?: string;
}> = ({ dates, setDates, testId = `calendar` }) => {
  const [value, setValue] = React.useState(dates);
  const valueRef = React.useRef(value);
  React.useEffect(() => {
    if (value !== valueRef.current) {
      setDates(value);
      valueRef.current = value;
    }
  }, [dates, setDates, value]);

  return (
    <Calendar
      modifiers={{
        range_start: (day: Date) =>
          value.length === 2 && isSameDay(day, value[0]),
        range_end: (day: Date) =>
          value.length === 2 && isSameDay(day, value[1]),
        range_middle: (day: Date) =>
          value.length === 2 &&
          isAfter(day, value[0]) &&
          isBefore(day, value[1]),
        selected: (day: Date) => {
          if (value.length === 1) {
            return isSameDay(day, value[0]);
          }
          if (value.length === 2) {
            // Include start, end, AND everything in between
            return (
              isSameDay(day, value[0]) ||
              isSameDay(day, value[1]) ||
              (isAfter(day, value[0]) && isBefore(day, value[1]))
            );
          }
          return false;
        },
      }}
      numberOfMonths={2}
      onDayClick={(day) =>
        setValue((prev) =>
          prev.length === 2 || prev === dates
            ? [day]
            : [...new Set([...prev, day].map((d) => d.getTime()))]
                .sort()
                .map((t) => new Date(t))
        )
      }
      selected={(() => {
        if (value.length === 1) return value[0];
        if (value.length === 2) return { from: value[0], to: value[1] };
      })()}
      data-testid={testId}
    />
  );
};

const Popover: React.FC<{
  label: React.ReactNode;
  hasValue: boolean;
  onClear?: () => void;
  disabled?: boolean;
  testId?: string;
  children?: React.ReactNode;
}> = ({ label, hasValue, onClear, disabled, children, testId = `popover` }) => {
  const theme = useTheme();
  const { isOpen, setOpen, dialog, trigger } = usePopover({
    placement: 'bottom-start',
    modifiers: [
      { name: 'offset', options: { offset: [0, theme.spacing.small] } },
    ],
  });

  return (
    <React.Fragment>
      <SelectMenuButton
        ref={trigger.ref}
        {...trigger.props}
        onClick={() => setOpen(!isOpen)}
        hasValue={hasValue}
        onClear={onClear}
        disabled={disabled}
        data-testid={testId}
      >
        {label}
      </SelectMenuButton>
      <PopoverDialog
        ref={dialog.ref}
        {...dialog.props}
        isVisible={isOpen}
        data-testid={makeId(testId, `dialog`)}
      >
        {isOpen && children}
      </PopoverDialog>
    </React.Fragment>
  );
};
